import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiError } from '../../api/client';
import { AuthProfile, Membership, MembershipRole, Tenant } from '../../api/types';
import { errorMessage, ptBR } from '../../i18n/pt-BR';

interface MembershipsResponse {
  memberships: Membership[];
}
interface MembershipResponse {
  membership: Membership;
}
interface TenantsResponse {
  tenants: Tenant[];
}
interface UsersResponse {
  users: AuthProfile[];
}

const roleOptions: MembershipRole[] = ['owner', 'member'];

export default function MembershipsPage() {
  const queryClient = useQueryClient();
  const membershipsQuery = useQuery({
    queryKey: ['admin', 'memberships'],
    queryFn: () => apiGet<MembershipsResponse>('/admin/memberships'),
  });
  const tenantsQuery = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiGet<TenantsResponse>('/admin/tenants'),
  });
  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiGet<UsersResponse>('/admin/users'),
  });

  const [userId, setUserId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [role, setRole] = useState<MembershipRole>('owner');
  const [error, setError] = useState<string | null>(null);

  const tenantsById = new Map((tenantsQuery.data?.tenants ?? []).map((t) => [t.id, t]));
  const usersById = new Map((usersQuery.data?.users ?? []).map((u) => [u.id, u]));

  const createMutation = useMutation({
    mutationFn: () =>
      apiPost<MembershipResponse>('/admin/memberships', { userId, tenantId, role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'memberships'] });
    },
    onError: (err) => setError(errorMessage(err instanceof ApiError ? err.code : undefined)),
  });

  function handleCreate(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <section>
      <h2>{ptBR.admin.memberships.title}</h2>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th>{ptBR.admin.memberships.person}</th>
            <th>{ptBR.admin.memberships.company}</th>
            <th>{ptBR.admin.memberships.role}</th>
          </tr>
        </thead>
        <tbody>
          {membershipsQuery.data?.memberships.map((m) => (
            <tr key={m.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td>{usersById.get(m.userId)?.name ?? m.userId}</td>
              <td>{tenantsById.get(m.tenantId)?.name ?? m.tenantId}</td>
              <td>{ptBR.admin.memberships.roleOptions[m.role]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>{ptBR.admin.memberships.createTitle}</h3>
      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
        <label>
          {ptBR.admin.memberships.person}
          <br />
          <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
            <option value="" disabled>
              …
            </option>
            {usersQuery.data?.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {ptBR.admin.memberships.company}
          <br />
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} required>
            <option value="" disabled>
              …
            </option>
            {tenantsQuery.data?.tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {ptBR.admin.memberships.role}
          <br />
          <select value={role} onChange={(e) => setRole(e.target.value as MembershipRole)}>
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {ptBR.admin.memberships.roleOptions[r]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={createMutation.isPending}>
          {ptBR.common.create}
        </button>
      </form>
      {error && <p style={{ color: '#c5221f' }}>{error}</p>}
    </section>
  );
}
